// src/schemas/zod/recording.ts
import * as z from "zod/v4";
var CreateVideoGridVideoSpecSchema = z.object({
  fromMs: z.number().int().nonnegative().optional(),
  toMs: z.number().int().nonnegative().optional(),
  segments: z.array(z.object({ id: z.string() })).min(1)
}).refine(({ fromMs, toMs }) => fromMs === void 0 || toMs === void 0 || fromMs < toMs, {
  message: "fromMs must be less than toMs"
});
var CreateVideoGridScheduleRequestSchema = z.object({
  videos: z.array(CreateVideoGridVideoSpecSchema).min(1)
});
var CreateVideoGridObjectLocationSchema = z.object({
  bucket: z.string().min(1),
  key: z.string().min(1)
});
var CreateVideoGridJobPayloadSchema = z.object({
  requestId: z.string().min(1),
  recordingId: z.string().min(1),
  input: z.object({
    videos: z.array(
      CreateVideoGridVideoSpecSchema.extend({
        segments: z.array(z.object({ file: CreateVideoGridObjectLocationSchema })).min(1)
      })
    ).min(1)
  }),
  output: CreateVideoGridObjectLocationSchema
});
var CreateVideoGridJobCallbackSchema = z.object({
  requestId: z.string().min(1),
  recordingId: z.string().min(1),
  status: z.enum(["success", "failure"])
});

// src/schemas/zod/analytics/context.ts
import { z as z3 } from "zod";

// src/schemas/zod/analytics/attributes.ts
import { z as z2 } from "zod";
var openEnum = (known) => Object.assign(z2.string(), { known });
var attr = {
  platform: z2.enum(["web_app", "browser_extension", "desktop_lockdown", "mobile_lockdown"]),
  lms: z2.enum(["canvas", "blackboard", "d2l", "moodle", "other"]),
  recordingMedium: z2.enum(["display", "webcam", "microphone"]),
  assessmentKind: z2.enum(["quiz", "assignment"]),
  sessionMode: z2.enum(["automated", "live"]),
  interruptType: z2.enum([
    "camera",
    "display",
    "screen_share_inactive",
    "monitor_count",
    "face_recorder",
    "speaking",
    "blocked_extension",
    "restricted_device",
    "handoff"
  ]),
  eligibilityFailureReason: z2.enum(["unsupported_platform", "access_denied", "configuration_missing"]),
  signatureMethod: openEnum(["drawn", "typed"]),
  setupStep: openEnum(["media", "room_scan", "signature", "identity"]),
  connectionMode: openEnum(["qr_code", "manual"]),
  startBlockedReason: openEnum(["already_started", "wrong_platform"]),
  completionResult: openEnum(["complete", "failed"]),
  reportKind: openEnum(["course", "institution", "assessment"]),
  reportFormat: openEnum(["json", "csv", "excel"]),
  reportDelivery: openEnum(["download", "email"])
};

// src/schemas/zod/analytics/context.ts
var strictPartialObject = (shape) => z3.strictObject(shape).partial();
var text = z3.union([z3.string(), z3.number()]).transform(String);
var brandVersion = z3.object({ brand: text, version: text });
var AnalyticsContextSchema = strictPartialObject({
  device: strictPartialObject({
    type: text,
    architecture: text,
    bitness: text,
    formFactors: z3.array(text),
    mobile: z3.boolean(),
    model: text
  }),
  os: strictPartialObject({
    name: text,
    version: text
  }),
  browser: strictPartialObject({
    name: text,
    version: text,
    brands: z3.array(brandVersion),
    fullVersionList: z3.array(brandVersion),
    engine: strictPartialObject({
      name: text,
      version: text
    }),
    userAgent: text,
    fingerprint: text
  }),
  network: strictPartialObject({
    status: z3.enum(["online", "offline"])
  }),
  hardware: strictPartialObject({
    memory: strictPartialObject({
      totalBytes: z3.number().int().nonnegative(),
      availableBytes: z3.number().int().nonnegative()
    }),
    cpu: strictPartialObject({
      logicalCores: z3.number().int().positive(),
      architecture: text,
      utilizationPercent: z3.number().min(0).max(100),
      pressure: text
    }),
    battery: strictPartialObject({
      chargePercent: z3.number().min(0).max(100),
      isCharging: z3.boolean(),
      chargingTimeSeconds: z3.number().int().nonnegative(),
      dischargingTimeSeconds: z3.number().int().nonnegative()
    }),
    thermals: strictPartialObject({
      pressure: text
    })
  }),
  app: strictPartialObject({
    name: z3.enum(["verity", "candidate"]),
    platform: attr.platform,
    version: text
  })
});

// src/schemas/zod/analytics/events/index.ts
import { z as z12 } from "zod";

// src/schemas/zod/analytics/events/assessment.events.ts
import { z as z6 } from "zod";

// src/schemas/zod/analytics/subject.ts
import { z as z4 } from "zod";
var positiveId = z4.number().int().positive().max(Number.MAX_SAFE_INTEGER);
var uuidString = z4.string().min(1);
var privilegeOperation = z4.enum(["create", "read", "update", "delete"]);
var variantSchemas = (formats, type) => Object.entries(formats).map(
  ([format, fields]) => z4.object({
    ...type === void 0 ? {} : { type: z4.literal(type) },
    format: z4.literal(format),
    ...fields.shape
  }).strict()
);
var defineAnalyticsSubject = (type, formats) => ({
  type,
  schema: z4.discriminatedUnion("format", variantSchemas(formats, type)),
  selectorSchema: z4.discriminatedUnion("format", variantSchemas(formats))
});
var subjectOneOf = (...definitions) => ({
  schema: z4.discriminatedUnion(
    "type",
    definitions.map(({ schema }) => schema)
  )
});
var noneSubjectFormats = { none: z4.object({}) };
var NoneSubject = defineAnalyticsSubject("none", noneSubjectFormats);
var ANALYTICS_NONE_SUBJECT = { type: "none", format: "none" };
var instituteSubjectFormats = {
  internal_id: z4.object({ instituteId: positiveId }),
  access_control_uuid: z4.object({ instituteUuid: uuidString })
};
var InstituteSubject = defineAnalyticsSubject("institute", instituteSubjectFormats);
var userSubjectFormats = {
  internal_id: z4.object({ userId: positiveId }),
  access_control_uuid: z4.object({ userUuid: uuidString }),
  lms_id: z4.object({ lmsUserId: z4.string(), institute: InstituteSubject.selectorSchema })
};
var UserSubject = defineAnalyticsSubject("user", userSubjectFormats);
var courseSubjectFormats = {
  internal_id: z4.object({ courseId: positiveId }),
  access_control_uuid: z4.object({ courseUuid: uuidString }),
  lms_id: z4.object({ lmsCourseId: z4.string(), institute: InstituteSubject.selectorSchema })
};
var CourseSubject = defineAnalyticsSubject("course", courseSubjectFormats);
var assessmentSubjectFormats = {
  internal_id: z4.object({ assessmentId: positiveId }),
  lms_id: z4.object({ lmsAssessmentId: z4.string(), course: CourseSubject.selectorSchema })
};
var AssessmentSubject = defineAnalyticsSubject("assessment", assessmentSubjectFormats);
var proctoringSessionSubjectFormats = {
  internal_id: z4.object({ proctoringSessionId: positiveId }),
  // Despite the name this is a base64url-encoded uuid, not canonical form: "dwkBh31eTuytWxv8EevimA".
  session_uuid: z4.object({ proctoringSessionUuid: z4.string() })
};
var ProctoringSessionSubject = defineAnalyticsSubject("proctoring_session", proctoringSessionSubjectFormats);
var liveSessionSubjectFormats = {
  internal_id: z4.object({ liveSessionId: positiveId })
};
var LiveSessionSubject = defineAnalyticsSubject("live_session", liveSessionSubjectFormats);
var proctoringAssessmentConfigurationSubjectFormats = {
  internal_id: z4.object({ proctoringAssessmentConfigurationId: positiveId }),
  assessment: z4.object({ assessment: AssessmentSubject.selectorSchema })
};
var ProctoringAssessmentConfigurationSubject = defineAnalyticsSubject("proctoring_assessment_configuration", proctoringAssessmentConfigurationSubjectFormats);
var privilegeSubjectFormats = {
  access_control_id: z4.object({
    privilegeId: positiveId,
    resource: z4.string(),
    operation: privilegeOperation
  })
};
var PrivilegeSubject = defineAnalyticsSubject("privilege", privilegeSubjectFormats);
var privilegeGroupSubjectFormats = {
  access_control_id: z4.object({ privilegeGroupId: positiveId, instituteUuid: uuidString })
};
var PrivilegeGroupSubject = defineAnalyticsSubject("privilege_group", privilegeGroupSubjectFormats);
var analyticsSubjectDefinitions = {
  none: NoneSubject,
  institute: InstituteSubject,
  user: UserSubject,
  course: CourseSubject,
  assessment: AssessmentSubject,
  proctoring_session: ProctoringSessionSubject,
  live_session: LiveSessionSubject,
  proctoring_assessment_configuration: ProctoringAssessmentConfigurationSubject,
  privilege: PrivilegeSubject,
  privilege_group: PrivilegeGroupSubject
};
var ANALYTICS_SUBJECT_TYPES = Object.keys(analyticsSubjectDefinitions);
var ANALYTICS_AFFECTED_SUBJECT_TYPES = ANALYTICS_SUBJECT_TYPES.filter(
  (type) => type !== "none"
);
var AnalyticsAffectedSubjectTypeSchema = z4.enum(ANALYTICS_AFFECTED_SUBJECT_TYPES);
var AnalyticsProctoringSchema = z4.object({
  proctoringSession: ProctoringSessionSubject.selectorSchema.optional(),
  liveSession: LiveSessionSubject.selectorSchema.optional()
}).strict().refine(({ proctoringSession, liveSession }) => proctoringSession !== void 0 || liveSession !== void 0, {
  message: "proctoring requires a proctoring or live session selector"
});

// src/schemas/zod/analytics/events/common.ts
import { z as z5 } from "zod";
var ANALYTICS_EVENT_SOURCES = ["client", "server"];
var ANALYTICS_OUTCOMES = ["success", "failure"];
var MAX_DETAIL_KEYS = 16;
var MAX_DETAIL_VALUE_CHARS = 2048;
var MAX_DETAILS_BYTES = 4 * 1024;
var utf8Length = (value) => new TextEncoder().encode(JSON.stringify(value)).length;
var AnalyticsDetailsSchema = z5.record(z5.string().min(1).max(100), z5.union([z5.string().max(MAX_DETAIL_VALUE_CHARS), z5.number(), z5.boolean()])).refine((details) => Object.keys(details).length <= MAX_DETAIL_KEYS, {
  message: `details must contain at most ${MAX_DETAIL_KEYS} keys`
}).refine((details) => utf8Length(details) <= MAX_DETAILS_BYTES, {
  message: `details must not exceed ${MAX_DETAILS_BYTES} bytes`
});
var payloadBranch = (outcome, data) => z5.object({
  outcome,
  data: z5.object(data).strict(),
  details: AnalyticsDetailsSchema.default({})
}).strict();
var payload = (data) => ({ schema: payloadBranch(z5.null().default(null), data), outcomes: null });
var payloadByOutcome = ({
  shared,
  success,
  failure
}) => {
  const withShared = (branch) => ({ ...shared, ...branch });
  return {
    schema: z5.discriminatedUnion("outcome", [
      payloadBranch(z5.literal("success"), withShared(success)),
      payloadBranch(z5.literal("failure"), withShared(failure))
    ]),
    outcomes: ANALYTICS_OUTCOMES
  };
};
var defineClientEvent = (configuration) => ({ ...configuration, source: "client" });
var defineServerEvent = (configuration) => ({ ...configuration, source: "server" });
var eventsAbout = (subject) => ({
  client: (action, payload2) => defineClientEvent({ action, subject, payload: payload2 }),
  server: (action, payload2) => defineServerEvent({ action, subject, payload: payload2 })
});
var nonNegativeInt = z5.number().int().nonnegative();
var schemaCache = /* @__PURE__ */ new WeakMap();
var schemaOf = (definition) => {
  const cached = schemaCache.get(definition);
  if (cached) return cached;
  const schema = z5.object({
    eventUuid: uuidString,
    timestamp: z5.coerce.date(),
    source: z5.literal(definition.source),
    action: z5.literal(definition.action),
    payload: definition.payload.schema,
    subject: definition.subject.schema,
    proctoring: AnalyticsProctoringSchema.optional(),
    context: AnalyticsContextSchema.optional()
  }).strict();
  schemaCache.set(definition, schema);
  return schema;
};
var storedSchemaOf = (definition) => schemaOf(definition).omit({ subject: true, proctoring: true });

// src/schemas/zod/analytics/events/assessment.events.ts
var assessment = eventsAbout(AssessmentSubject);
var AssessmentLaunchDetectedEvent = assessment.client(
  "assessment.launch.detected",
  payload({ lms: attr.lms, assessmentKind: attr.assessmentKind })
);
var AssessmentStartRequestedEvent = assessment.client(
  "assessment.start.requested",
  payload({ entryPoint: z6.string() })
);
var AssessmentCompatibilityCheckStartedEvent = assessment.client(
  "assessment.compatibility_check.started",
  payload({ platform: attr.platform })
);
var AssessmentStartBlockedEvent = assessment.client(
  "assessment.start.blocked",
  payload({ reason: attr.startBlockedReason, detail: z6.string().optional() })
);
var AssessmentPreviewOpenedEvent = assessment.client("assessment.preview.opened", payload({}));
var AssessmentPreviewOptionSelectedEvent = assessment.client(
  "assessment.preview.option.selected",
  payload({ selection: z6.enum(["start", "practice", "cancel"]) })
);
var AssessmentEligibilityCheckedEvent = assessment.server(
  "assessment.eligibility.checked",
  payloadByOutcome({ success: {}, failure: { reason: attr.eligibilityFailureReason } })
);
var AssessmentExemptionAppliedEvent = assessment.server("assessment.exemption.applied", payload({}));
var AssessmentConfigurationUpdatedEvent = eventsAbout(ProctoringAssessmentConfigurationSubject).server(
  "assessment.configuration.updated",
  payload({})
);
var AssessmentProctoringToggledEvent = assessment.client(
  "assessment.proctoring.toggled",
  payload({ enabled: z6.boolean() })
);
var AssessmentConfigurationSaveFailedEvent = assessment.client(
  "assessment.configuration.save.failed",
  payload({})
);
var assessmentEventDefinitions = [
  AssessmentLaunchDetectedEvent,
  AssessmentStartRequestedEvent,
  AssessmentCompatibilityCheckStartedEvent,
  AssessmentStartBlockedEvent,
  AssessmentPreviewOpenedEvent,
  AssessmentPreviewOptionSelectedEvent,
  AssessmentEligibilityCheckedEvent,
  AssessmentExemptionAppliedEvent,
  AssessmentConfigurationUpdatedEvent,
  AssessmentProctoringToggledEvent,
  AssessmentConfigurationSaveFailedEvent
];

// src/schemas/zod/analytics/events/auth.events.ts
import { z as z7 } from "zod";
var user = eventsAbout(UserSubject);
var AuthLoginEvent = user.server("auth.login", payload({ method: z7.enum(["basic_auth", "lti_1p3", "saml"]) }));
var AuthTokenRefreshedEvent = user.client(
  "auth.token.refreshed",
  payloadByOutcome({ shared: { status: z7.number().int().optional() }, success: {}, failure: {} })
);
var authEventDefinitions = [AuthLoginEvent, AuthTokenRefreshedEvent];

// src/schemas/zod/analytics/events/institute.events.ts
import { z as z8 } from "zod";
var institute = eventsAbout(InstituteSubject);
var named = payload({ instituteName: z8.string() });
var InstituteCreatedEvent = institute.server("institute.created", named);
var InstituteUpdatedEvent = institute.server("institute.updated", named);
var InstituteDeletedEvent = institute.server("institute.deleted", named);
var instituteEventDefinitions = [InstituteCreatedEvent, InstituteUpdatedEvent, InstituteDeletedEvent];

// src/schemas/zod/analytics/events/portal.events.ts
import { z as z9 } from "zod";
var PortalLaunchedEvent = eventsAbout(subjectOneOf(CourseSubject, InstituteSubject)).client(
  "portal.launched",
  payload({
    entryPoint: z9.enum(["lti", "institution_select", "direct"]),
    embedded: z9.boolean()
  })
);
var portalEventDefinitions = [PortalLaunchedEvent];

// src/schemas/zod/analytics/events/privilege.events.ts
import { z as z10 } from "zod";
var privilege = eventsAbout(PrivilegeSubject);
var privilegeGroup = eventsAbout(PrivilegeGroupSubject);
var named2 = payload({ groupName: z10.string() });
var PrivilegeCreatedEvent = privilege.server("privilege.created", payload({}));
var PrivilegeDeletedEvent = privilege.server("privilege.deleted", payload({}));
var PrivilegeGroupCreatedEvent = privilegeGroup.server("privilege_group.created", named2);
var PrivilegeGroupUpdatedEvent = privilegeGroup.server("privilege_group.updated", named2);
var PrivilegeGroupDeletedEvent = privilegeGroup.server("privilege_group.deleted", named2);
var privilegeEventDefinitions = [
  PrivilegeCreatedEvent,
  PrivilegeDeletedEvent,
  PrivilegeGroupCreatedEvent,
  PrivilegeGroupUpdatedEvent,
  PrivilegeGroupDeletedEvent
];

// src/schemas/zod/analytics/events/proctoring.events.ts
import { z as z11 } from "zod";
var session = eventsAbout(ProctoringSessionSubject);
var mediaFailure = z11.enum(["noPermission", "notFound", "unknown"]);
var passOrFail = payloadByOutcome({ success: {}, failure: {} });
var mediaCheck = payloadByOutcome({ success: {}, failure: { reason: mediaFailure } });
var ProctoringSetupOpenedEvent = session.client(
  "proctoring.setup.opened",
  payload({ checkCount: nonNegativeInt })
);
var ProctoringSetupSystemResourcesCheckedEvent = session.client(
  "proctoring.setup.system_resources.checked",
  payloadByOutcome({
    shared: { cpuPercent: z11.number().optional(), memoryPercent: z11.number().optional() },
    success: {},
    failure: {}
  })
);
var ProctoringSetupConnectionCheckedEvent = session.client(
  "proctoring.setup.connection.checked",
  payloadByOutcome({
    shared: { uploadedBps: z11.number().nonnegative(), requiredBps: z11.number().nonnegative() },
    success: {},
    failure: {}
  })
);
var ProctoringSetupGeofenceCheckedEvent = session.client(
  "proctoring.setup.geofence.checked",
  payloadByOutcome({ success: {}, failure: { reason: z11.enum(["notInRadius", "noPermission", "unavailable"]) } })
);
var ProctoringSetupIncognitoCheckedEvent = session.client("proctoring.setup.incognito.checked", passOrFail);
var ProctoringSetupManagedGuestSessionCheckedEvent = session.client(
  "proctoring.setup.managed_guest_session.checked",
  passOrFail
);
var ProctoringSetupBlockedExtensionsCheckedEvent = session.client(
  "proctoring.setup.blocked_extensions.checked",
  payloadByOutcome({ success: {}, failure: { blockedCount: nonNegativeInt.optional() } })
);
var ProctoringSetupSidePanelCheckedEvent = session.client("proctoring.setup.side_panel.checked", passOrFail);
var ProctoringSetupMonitorCountCheckedEvent = session.client(
  "proctoring.setup.monitor_count.checked",
  passOrFail
);
var ProctoringSetupBrowserSupportedCheckedEvent = session.client(
  "proctoring.setup.browser_supported.checked",
  payloadByOutcome({ success: {}, failure: { reason: z11.string() } })
);
var ProctoringSetupScreenShareCheckedEvent = session.client(
  "proctoring.setup.screen_share.checked",
  mediaCheck
);
var ProctoringSetupCameraCheckedEvent = session.client("proctoring.setup.camera.checked", mediaCheck);
var ProctoringSetupMicrophoneCheckedEvent = session.client("proctoring.setup.microphone.checked", mediaCheck);
var ProctoringSetupSecondaryCameraCheckedEvent = session.client(
  "proctoring.setup.secondary_camera.checked",
  payloadByOutcome({ shared: { connectionMode: attr.connectionMode }, success: {}, failure: {} })
);
var ProctoringSetupMobileDeviceConnectedEvent = session.client(
  "proctoring.setup.mobile_device.connected",
  payloadByOutcome({ shared: { connectionMode: attr.connectionMode }, success: {}, failure: {} })
);
var ProctoringSetupRoomScanCompletedEvent = session.client("proctoring.setup.room_scan.completed", passOrFail);
var ProctoringSetupIdentityCompletedEvent = session.client(
  "proctoring.setup.identity.completed",
  payloadByOutcome({ shared: { requiredCheckCount: nonNegativeInt, aiUsed: z11.boolean() }, success: {}, failure: {} })
);
var ProctoringSetupSignatureCompletedEvent = session.client(
  "proctoring.setup.signature.completed",
  payloadByOutcome({ shared: { method: attr.signatureMethod }, success: {}, failure: {} })
);
var ProctoringSetupCancelledEvent = session.client(
  "proctoring.setup.cancelled",
  payload({ activeStep: attr.setupStep, thirdParty: z11.boolean() })
);
var ProctoringSetupCompletedEvent = session.client(
  "proctoring.setup.completed",
  payload({ failedCheckCount: nonNegativeInt })
);
var ProctoringAssessmentStartedEvent = session.server(
  "proctoring.assessment.started",
  payload({ fromSetup: z11.boolean() })
);
var ProctoringAssessmentSubmissionRequestedEvent = session.client(
  "proctoring.assessment.submission.requested",
  payload({ forced: z11.boolean(), timerExpired: z11.boolean() })
);
var ProctoringAssessmentSubmissionFailedEvent = session.client(
  "proctoring.assessment.submission.failed",
  payload({ attemptCount: nonNegativeInt })
);
var ProctoringAssessmentPasswordRejectedEvent = session.client(
  "proctoring.assessment.password.rejected",
  payload({})
);
var ProctoringAssessmentSubmittedEvent = session.server("proctoring.assessment.submitted", payload({}));
var ProctoringAssessmentLmsNotifiedEvent = session.client(
  "proctoring.assessment.lms_notified",
  payload({ lms: attr.lms })
);
var ProctoringRecordingStartedEvent = session.server(
  "proctoring.recording.started",
  payload({ medium: attr.recordingMedium, method: z11.enum(["local", "streaming"]), sourceIndex: nonNegativeInt })
);
var ProctoringRecordingStoppedEvent = session.server(
  "proctoring.recording.stopped",
  payload({ medium: attr.recordingMedium, sourceIndex: nonNegativeInt })
);
var ProctoringRecordingUploadCompletedEvent = session.server(
  "proctoring.recording.upload.completed",
  payloadByOutcome({ shared: { medium: attr.recordingMedium }, success: {}, failure: {} })
);
var ProctoringRecordingUploadFailedEvent = session.client(
  "proctoring.recording.upload.failed",
  payload({ medium: attr.recordingMedium, stage: z11.enum(["prepare", "upload", "finalize"]) })
);
var ProctoringScratchNoteUploadCompletedEvent = session.server(
  "proctoring.scratch_note.upload.completed",
  payload({})
);
var ProctoringRestrictionNewTabsAppliedEvent = session.client(
  "proctoring.restriction.new_tabs.applied",
  passOrFail
);
var ProctoringRestrictionClipboardAppliedEvent = session.client(
  "proctoring.restriction.clipboard.applied",
  passOrFail
);
var ProctoringRestrictionRightClickAppliedEvent = session.client(
  "proctoring.restriction.right_click.applied",
  passOrFail
);
var ProctoringRestrictionPrintingAppliedEvent = session.client(
  "proctoring.restriction.printing.applied",
  passOrFail
);
var ProctoringRestrictionWebTrafficAppliedEvent = session.client(
  "proctoring.restriction.web_traffic.applied",
  passOrFail
);
var ProctoringRestrictionOpenTabsAppliedEvent = session.client(
  "proctoring.restriction.open_tabs.applied",
  passOrFail
);
var ProctoringRestrictionRightClickBlockedEvent = session.server(
  "proctoring.restriction.right_click.blocked",
  payload({})
);
var ProctoringRestrictionPrintingBlockedEvent = session.server(
  "proctoring.restriction.printing.blocked",
  payload({})
);
var ProctoringNavigationBlockedEvent = session.server(
  "proctoring.navigation.blocked",
  payload({ reason: z11.string().optional(), method: z11.string().optional() })
);
var ProctoringRestrictionSidePanelDetectedEvent = session.server(
  "proctoring.restriction.side_panel.detected",
  payload({})
);
var ProctoringRestrictionBlockedExtensionDetectedEvent = session.server(
  "proctoring.restriction.blocked_extension.detected",
  payload({})
);
var ProctoringSessionStartedEvent = session.server(
  "proctoring.session.started",
  payload({ platform: attr.platform, sessionMode: attr.sessionMode })
);
var ProctoringSessionEndedEvent = session.server(
  "proctoring.session.ended",
  payload({ endReason: z11.string() })
);
var ProctoringSessionFocusLostEvent = session.server("proctoring.session.focus.lost", payload({}));
var ProctoringSessionMonitorCountChangedEvent = session.server(
  "proctoring.session.monitor_count.changed",
  payload({ displayCount: nonNegativeInt })
);
var ProctoringInterruptTriggeredEvent = session.server(
  "proctoring.interrupt.triggered",
  payload({ type: attr.interruptType })
);
var ProctoringInterruptResolvedEvent = session.server(
  "proctoring.interrupt.resolved",
  payload({ type: attr.interruptType })
);
var ProctoringNetworkDisconnectedEvent = session.client("proctoring.network.disconnected", payload({}));
var ProctoringNetworkConnectedEvent = session.client("proctoring.network.connected", payload({}));
var ProctoringBrowserClosedEvent = session.client("proctoring.browser.closed", payload({}));
var ProctoringLiveSessionJoinedEvent = session.server(
  "proctoring.live.session.joined",
  payload({ proctorCount: nonNegativeInt, groupSession: z11.boolean() })
);
var ProctoringLiveAudioBridgeReadyEvent = session.server("proctoring.live.audio_bridge.ready", passOrFail);
var ProctoringLiveProctorJoinedEvent = session.server(
  "proctoring.live.proctor.joined",
  payload({ proctorCount: nonNegativeInt })
);
var ProctoringLiveProctorLeftEvent = session.server(
  "proctoring.live.proctor.left",
  payload({ proctorCount: nonNegativeInt })
);
var ProctoringLiveNoProctorAvailableEvent = session.server(
  "proctoring.live.no_proctor.available",
  payload({ onJoin: z11.boolean() })
);
var ProctoringCompletionUploadOpenedEvent = session.client("proctoring.completion.upload.opened", payload({}));
var ProctoringCompletionDisplayedEvent = session.client(
  "proctoring.completion.displayed",
  payload({ result: attr.completionResult })
);
var ProctoringCompletionDismissedEvent = session.client(
  "proctoring.completion.dismissed",
  payload({ method: z11.enum(["close", "reload"]) })
);
var proctoringEventDefinitions = [
  ProctoringSetupOpenedEvent,
  ProctoringSetupSystemResourcesCheckedEvent,
  ProctoringSetupConnectionCheckedEvent,
  ProctoringSetupGeofenceCheckedEvent,
  ProctoringSetupIncognitoCheckedEvent,
  ProctoringSetupManagedGuestSessionCheckedEvent,
  ProctoringSetupBlockedExtensionsCheckedEvent,
  ProctoringSetupSidePanelCheckedEvent,
  ProctoringSetupMonitorCountCheckedEvent,
  ProctoringSetupBrowserSupportedCheckedEvent,
  ProctoringSetupScreenShareCheckedEvent,
  ProctoringSetupCameraCheckedEvent,
  ProctoringSetupMicrophoneCheckedEvent,
  ProctoringSetupSecondaryCameraCheckedEvent,
  ProctoringSetupMobileDeviceConnectedEvent,
  ProctoringSetupRoomScanCompletedEvent,
  ProctoringSetupIdentityCompletedEvent,
  ProctoringSetupSignatureCompletedEvent,
  ProctoringSetupCancelledEvent,
  ProctoringSetupCompletedEvent,
  ProctoringAssessmentStartedEvent,
  ProctoringAssessmentSubmissionRequestedEvent,
  ProctoringAssessmentSubmissionFailedEvent,
  ProctoringAssessmentPasswordRejectedEvent,
  ProctoringAssessmentSubmittedEvent,
  ProctoringAssessmentLmsNotifiedEvent,
  ProctoringRecordingStartedEvent,
  ProctoringRecordingStoppedEvent,
  ProctoringRecordingUploadCompletedEvent,
  ProctoringRecordingUploadFailedEvent,
  ProctoringScratchNoteUploadCompletedEvent,
  ProctoringRestrictionNewTabsAppliedEvent,
  ProctoringRestrictionClipboardAppliedEvent,
  ProctoringRestrictionRightClickAppliedEvent,
  ProctoringRestrictionPrintingAppliedEvent,
  ProctoringRestrictionWebTrafficAppliedEvent,
  ProctoringRestrictionOpenTabsAppliedEvent,
  ProctoringRestrictionRightClickBlockedEvent,
  ProctoringRestrictionPrintingBlockedEvent,
  ProctoringNavigationBlockedEvent,
  ProctoringRestrictionSidePanelDetectedEvent,
  ProctoringRestrictionBlockedExtensionDetectedEvent,
  ProctoringSessionStartedEvent,
  ProctoringSessionEndedEvent,
  ProctoringSessionFocusLostEvent,
  ProctoringSessionMonitorCountChangedEvent,
  ProctoringInterruptTriggeredEvent,
  ProctoringInterruptResolvedEvent,
  ProctoringNetworkDisconnectedEvent,
  ProctoringNetworkConnectedEvent,
  ProctoringBrowserClosedEvent,
  ProctoringLiveSessionJoinedEvent,
  ProctoringLiveAudioBridgeReadyEvent,
  ProctoringLiveProctorJoinedEvent,
  ProctoringLiveProctorLeftEvent,
  ProctoringLiveNoProctorAvailableEvent,
  ProctoringCompletionUploadOpenedEvent,
  ProctoringCompletionDisplayedEvent,
  ProctoringCompletionDismissedEvent
];

// src/schemas/zod/analytics/events/reports.events.ts
var ReportsExportedEvent = eventsAbout(InstituteSubject).client(
  "reports.exported",
  payload({ reportType: attr.reportKind, format: attr.reportFormat, delivery: attr.reportDelivery })
);
var reportsEventDefinitions = [ReportsExportedEvent];

// src/schemas/zod/analytics/events/results.events.ts
var ResultsSessionViewedEvent = eventsAbout(ProctoringSessionSubject).client(
  "results.session.viewed",
  payload({})
);
var resultsEventDefinitions = [ResultsSessionViewedEvent];

// src/schemas/zod/analytics/events/index.ts
var ANALYTICS_EVENT_DEFINITIONS = [
  ...authEventDefinitions,
  ...assessmentEventDefinitions,
  ...proctoringEventDefinitions,
  ...instituteEventDefinitions,
  ...privilegeEventDefinitions,
  ...portalEventDefinitions,
  ...reportsEventDefinitions,
  ...resultsEventDefinitions
];
var ANALYTICS_ACTIONS = ANALYTICS_EVENT_DEFINITIONS.map(
  ({ action }) => action
);
var ANALYTICS_DEFINITIONS_BY_ACTION = Object.fromEntries(
  ANALYTICS_EVENT_DEFINITIONS.map((definition) => [definition.action, definition])
);
var definitionsBySource = (source) => ANALYTICS_EVENT_DEFINITIONS.filter((definition) => definition.source === source);
var unionOf = (schemas) => z12.discriminatedUnion("action", schemas);
var ANALYTICS_CLIENT_EVENT_DEFINITIONS = definitionsBySource("client");
var ANALYTICS_SERVER_EVENT_DEFINITIONS = definitionsBySource("server");
var AnalyticsClientEventSchema = unionOf(
  ANALYTICS_CLIENT_EVENT_DEFINITIONS.map(schemaOf)
);
var AnalyticsServerEventSchema = unionOf(
  ANALYTICS_SERVER_EVENT_DEFINITIONS.map(schemaOf)
);
var AnalyticsStoredEventDataSchema = z12.discriminatedUnion("source", [
  unionOf(ANALYTICS_CLIENT_EVENT_DEFINITIONS.map(storedSchemaOf)),
  unionOf(ANALYTICS_SERVER_EVENT_DEFINITIONS.map(storedSchemaOf))
]);

// src/schemas/zod/analytics/producer.ts
var describeFailure = (error) => error.issues.map((issue) => `${issue.path.join(".") || "event"}: ${issue.message}`).join("; ");
var createEventWithMetadata = (definition, input, metadata) => {
  const parsed = schemaOf(definition).safeParse({
    ...input,
    eventUuid: metadata.eventUuid,
    timestamp: metadata.timestamp,
    source: definition.source,
    action: definition.action
  });
  return parsed.success ? { ok: true, event: parsed.data } : { ok: false, action: definition.action, reason: describeFailure(parsed.error) };
};

// src/schemas/zod/analytics/api.ts
import { z as z13 } from "zod";
var positiveId2 = z13.number().int().positive();
var externalReference = z13.string().trim().min(1).max(255);
var MAX_DATE_RANGE_MS = 366 * 24 * 60 * 60 * 1e3;
var MAX_MESSAGE_QUERY_LENGTH = 200;
var MAX_SUGGESTION_QUERY_LENGTH = 100;
var MAX_FILTER_SUGGESTIONS_PER_KIND = 5;
var MAX_FILTER_SUGGESTIONS = 15;
var isOrderedDateRange = ({ startTime, endTime }) => startTime <= endTime;
var isBoundedDateRange = ({ startTime, endTime }) => endTime.getTime() - startTime.getTime() <= MAX_DATE_RANGE_MS;
var AnalyticsDateRangeSchema = z13.object({ startTime: z13.date(), endTime: z13.date() }).strict().refine(isOrderedDateRange, { message: "startTime must be before or equal to endTime", path: ["endTime"] }).refine(isBoundedDateRange, { message: "date range must not exceed 366 days", path: ["endTime"] });
var AnalyticsIngestOneServerEventSchema = z13.object({
  auth: z13.discriminatedUnion("method", [
    z13.object({ method: z13.literal("recordingUuid"), recordingUuid: externalReference }).strict(),
    z13.object({ method: z13.literal("userUuid"), userUuid: uuidString }).strict(),
    z13.object({ method: z13.literal("userId"), userId: positiveId2 }).strict()
  ]),
  event: AnalyticsServerEventSchema
}).strict();
var AnalyticsIngestOneOutputSchema = z13.object({ eventUuid: uuidString }).strict();
var ANALYTICS_MAX_BATCH_EVENTS = 25;
var AnalyticsIngestClientEventBatchSchema = z13.object({
  auth: z13.discriminatedUnion("method", [z13.object({ method: z13.literal("jwt") }).strict()]),
  events: z13.array(AnalyticsClientEventSchema).min(1).max(ANALYTICS_MAX_BATCH_EVENTS)
}).strict();
var AnalyticsIngestBatchResultSchema = z13.discriminatedUnion("status", [
  z13.object({ status: z13.literal("accepted"), eventUuid: uuidString }).strict(),
  z13.object({ status: z13.literal("rejected"), eventUuid: uuidString, reason: z13.string() }).strict()
]);
var AnalyticsIngestBatchOutputSchema = z13.object({ results: z13.array(AnalyticsIngestBatchResultSchema) }).strict();
var AnalyticsResolvedCorrelationSchema = z13.object({
  instituteUuid: uuidString.optional(),
  userUuid: uuidString.optional(),
  courseId: positiveId2.optional(),
  assessmentId: positiveId2.optional(),
  proctoringSessionId: positiveId2.optional(),
  liveSessionId: positiveId2.optional()
}).strict();
var AnalyticsResolvedSubjectSchema = z13.object({
  type: AnalyticsAffectedSubjectTypeSchema,
  id: z13.string().trim().min(1).max(255),
  correlation: AnalyticsResolvedCorrelationSchema
}).strict();
var boundedFilterList = (schema) => z13.array(schema).max(ANALYTICS_ACTIONS.length);
var AnalyticsQueryFiltersSchema = z13.object({
  actions: boundedFilterList(z13.enum(ANALYTICS_ACTIONS)).optional(),
  sources: boundedFilterList(z13.enum(ANALYTICS_EVENT_SOURCES)).optional(),
  outcomes: boundedFilterList(z13.enum(ANALYTICS_OUTCOMES)).optional(),
  message: z13.string().trim().max(MAX_MESSAGE_QUERY_LENGTH).optional(),
  userUuids: boundedFilterList(uuidString).optional(),
  courseIds: boundedFilterList(positiveId2).optional(),
  assessmentIds: boundedFilterList(positiveId2).optional(),
  proctoringSessionIds: boundedFilterList(positiveId2).optional(),
  liveSessionIds: boundedFilterList(positiveId2).optional(),
  dateRange: AnalyticsDateRangeSchema
}).strict();
var AnalyticsCursorSchema = z13.object({
  timestamp: z13.date(),
  eventUuid: uuidString
}).strict();
var AnalyticsQueryInputSchema = z13.object({
  instituteUuid: uuidString,
  filters: AnalyticsQueryFiltersSchema,
  sortOrder: z13.enum(["asc", "desc"]).optional(),
  pagination: z13.object({
    limit: z13.number().int().min(1).max(100).default(25),
    cursor: AnalyticsCursorSchema.optional()
  }).strict().default({ limit: 25 })
}).strict();
var AnalyticsActorSchema = z13.object({
  userUuid: uuidString,
  instituteUuid: uuidString
}).strict();
var jsonObject = z13.record(z13.string(), z13.unknown());
var AnalyticsActivitySchema = z13.object({
  eventUuid: uuidString,
  action: z13.enum(ANALYTICS_ACTIONS),
  source: z13.enum(ANALYTICS_EVENT_SOURCES),
  message: z13.string(),
  outcome: z13.enum(ANALYTICS_OUTCOMES).nullable(),
  timestamp: z13.date(),
  createdAt: z13.date(),
  actor: AnalyticsActorSchema,
  subject: AnalyticsResolvedSubjectSchema.optional(),
  proctoring: AnalyticsResolvedCorrelationSchema.optional(),
  data: jsonObject,
  details: jsonObject,
  context: jsonObject,
  ipAddress: z13.string()
}).strict();
var AnalyticsQueryOutputSchema = z13.object({
  events: z13.array(AnalyticsActivitySchema),
  nextCursor: AnalyticsCursorSchema.nullable()
}).strict();
var ANALYTICS_FILTER_SUGGESTION_KINDS = ["user", "course", "assessment"];
var AnalyticsFilterSuggestionKindSchema = z13.enum(ANALYTICS_FILTER_SUGGESTION_KINDS);
var filterSuggestionLabels = {
  label: z13.string().min(1).max(200),
  secondaryLabel: z13.string().min(1).max(320).optional()
};
var AnalyticsFilterSuggestionSchema = z13.discriminatedUnion("kind", [
  z13.object({ kind: z13.literal("user"), id: uuidString, ...filterSuggestionLabels }).strict(),
  z13.object({ kind: z13.literal("course"), id: positiveId2, ...filterSuggestionLabels }).strict(),
  z13.object({ kind: z13.literal("assessment"), id: positiveId2, ...filterSuggestionLabels }).strict()
]);
var AnalyticsFilterSuggestionsInputSchema = z13.object({
  instituteUuid: uuidString,
  query: z13.string().trim().min(2).max(MAX_SUGGESTION_QUERY_LENGTH),
  kinds: z13.array(AnalyticsFilterSuggestionKindSchema).min(1).max(ANALYTICS_FILTER_SUGGESTION_KINDS.length).optional()
}).strict();
var AnalyticsFilterSuggestionListSchema = z13.array(AnalyticsFilterSuggestionSchema).max(MAX_FILTER_SUGGESTIONS).refine(
  (suggestions) => ANALYTICS_FILTER_SUGGESTION_KINDS.every(
    (kind) => suggestions.filter((suggestion) => suggestion.kind === kind).length <= MAX_FILTER_SUGGESTIONS_PER_KIND
  ),
  { message: `suggestions must contain at most ${MAX_FILTER_SUGGESTIONS_PER_KIND} results per kind` }
);
var AnalyticsFilterSuggestionsOutputSchema = z13.object({ suggestions: AnalyticsFilterSuggestionListSchema }).strict();
var AnalyticsStatisticsInputSchema = z13.object({
  instituteUuid: uuidString,
  dateRange: AnalyticsDateRangeSchema
}).strict();
var eventCount = z13.number().int().nonnegative();
var AnalyticsStatisticsOutputSchema = z13.object({
  totalEvents: eventCount,
  totalLoginEvents: eventCount,
  totalProctoringEvents: eventCount,
  totalSessionsStarted: eventCount
}).strict();
export {
  ANALYTICS_ACTIONS,
  ANALYTICS_AFFECTED_SUBJECT_TYPES,
  ANALYTICS_CLIENT_EVENT_DEFINITIONS,
  ANALYTICS_DEFINITIONS_BY_ACTION,
  ANALYTICS_EVENT_DEFINITIONS,
  ANALYTICS_EVENT_SOURCES,
  ANALYTICS_FILTER_SUGGESTION_KINDS,
  ANALYTICS_MAX_BATCH_EVENTS,
  ANALYTICS_NONE_SUBJECT,
  ANALYTICS_OUTCOMES,
  ANALYTICS_SERVER_EVENT_DEFINITIONS,
  ANALYTICS_SUBJECT_TYPES,
  AnalyticsActivitySchema,
  AnalyticsActorSchema,
  AnalyticsAffectedSubjectTypeSchema,
  AnalyticsClientEventSchema,
  AnalyticsContextSchema,
  AnalyticsCursorSchema,
  AnalyticsDateRangeSchema,
  AnalyticsDetailsSchema,
  AnalyticsFilterSuggestionKindSchema,
  AnalyticsFilterSuggestionSchema,
  AnalyticsFilterSuggestionsInputSchema,
  AnalyticsFilterSuggestionsOutputSchema,
  AnalyticsIngestBatchOutputSchema,
  AnalyticsIngestBatchResultSchema,
  AnalyticsIngestClientEventBatchSchema,
  AnalyticsIngestOneOutputSchema,
  AnalyticsIngestOneServerEventSchema,
  AnalyticsProctoringSchema,
  AnalyticsQueryFiltersSchema,
  AnalyticsQueryInputSchema,
  AnalyticsQueryOutputSchema,
  AnalyticsResolvedCorrelationSchema,
  AnalyticsResolvedSubjectSchema,
  AnalyticsServerEventSchema,
  AnalyticsStatisticsInputSchema,
  AnalyticsStatisticsOutputSchema,
  AnalyticsStoredEventDataSchema,
  AssessmentCompatibilityCheckStartedEvent,
  AssessmentConfigurationSaveFailedEvent,
  AssessmentConfigurationUpdatedEvent,
  AssessmentEligibilityCheckedEvent,
  AssessmentExemptionAppliedEvent,
  AssessmentLaunchDetectedEvent,
  AssessmentPreviewOpenedEvent,
  AssessmentPreviewOptionSelectedEvent,
  AssessmentProctoringToggledEvent,
  AssessmentStartBlockedEvent,
  AssessmentStartRequestedEvent,
  AssessmentSubject,
  AuthLoginEvent,
  AuthTokenRefreshedEvent,
  CourseSubject,
  CreateVideoGridJobCallbackSchema,
  CreateVideoGridJobPayloadSchema,
  CreateVideoGridObjectLocationSchema,
  CreateVideoGridScheduleRequestSchema,
  CreateVideoGridVideoSpecSchema,
  InstituteCreatedEvent,
  InstituteDeletedEvent,
  InstituteSubject,
  InstituteUpdatedEvent,
  LiveSessionSubject,
  MAX_FILTER_SUGGESTIONS_PER_KIND,
  NoneSubject,
  PortalLaunchedEvent,
  PrivilegeCreatedEvent,
  PrivilegeDeletedEvent,
  PrivilegeGroupCreatedEvent,
  PrivilegeGroupDeletedEvent,
  PrivilegeGroupSubject,
  PrivilegeGroupUpdatedEvent,
  PrivilegeSubject,
  ProctoringAssessmentConfigurationSubject,
  ProctoringAssessmentLmsNotifiedEvent,
  ProctoringAssessmentPasswordRejectedEvent,
  ProctoringAssessmentStartedEvent,
  ProctoringAssessmentSubmissionFailedEvent,
  ProctoringAssessmentSubmissionRequestedEvent,
  ProctoringAssessmentSubmittedEvent,
  ProctoringBrowserClosedEvent,
  ProctoringCompletionDismissedEvent,
  ProctoringCompletionDisplayedEvent,
  ProctoringCompletionUploadOpenedEvent,
  ProctoringInterruptResolvedEvent,
  ProctoringInterruptTriggeredEvent,
  ProctoringLiveAudioBridgeReadyEvent,
  ProctoringLiveNoProctorAvailableEvent,
  ProctoringLiveProctorJoinedEvent,
  ProctoringLiveProctorLeftEvent,
  ProctoringLiveSessionJoinedEvent,
  ProctoringNavigationBlockedEvent,
  ProctoringNetworkConnectedEvent,
  ProctoringNetworkDisconnectedEvent,
  ProctoringRecordingStartedEvent,
  ProctoringRecordingStoppedEvent,
  ProctoringRecordingUploadCompletedEvent,
  ProctoringRecordingUploadFailedEvent,
  ProctoringRestrictionBlockedExtensionDetectedEvent,
  ProctoringRestrictionClipboardAppliedEvent,
  ProctoringRestrictionNewTabsAppliedEvent,
  ProctoringRestrictionOpenTabsAppliedEvent,
  ProctoringRestrictionPrintingAppliedEvent,
  ProctoringRestrictionPrintingBlockedEvent,
  ProctoringRestrictionRightClickAppliedEvent,
  ProctoringRestrictionRightClickBlockedEvent,
  ProctoringRestrictionSidePanelDetectedEvent,
  ProctoringRestrictionWebTrafficAppliedEvent,
  ProctoringScratchNoteUploadCompletedEvent,
  ProctoringSessionEndedEvent,
  ProctoringSessionFocusLostEvent,
  ProctoringSessionMonitorCountChangedEvent,
  ProctoringSessionStartedEvent,
  ProctoringSessionSubject,
  ProctoringSetupBlockedExtensionsCheckedEvent,
  ProctoringSetupBrowserSupportedCheckedEvent,
  ProctoringSetupCameraCheckedEvent,
  ProctoringSetupCancelledEvent,
  ProctoringSetupCompletedEvent,
  ProctoringSetupConnectionCheckedEvent,
  ProctoringSetupGeofenceCheckedEvent,
  ProctoringSetupIdentityCompletedEvent,
  ProctoringSetupIncognitoCheckedEvent,
  ProctoringSetupManagedGuestSessionCheckedEvent,
  ProctoringSetupMicrophoneCheckedEvent,
  ProctoringSetupMobileDeviceConnectedEvent,
  ProctoringSetupMonitorCountCheckedEvent,
  ProctoringSetupOpenedEvent,
  ProctoringSetupRoomScanCompletedEvent,
  ProctoringSetupScreenShareCheckedEvent,
  ProctoringSetupSecondaryCameraCheckedEvent,
  ProctoringSetupSidePanelCheckedEvent,
  ProctoringSetupSignatureCompletedEvent,
  ProctoringSetupSystemResourcesCheckedEvent,
  ReportsExportedEvent,
  ResultsSessionViewedEvent,
  UserSubject,
  analyticsSubjectDefinitions,
  assessmentEventDefinitions,
  assessmentSubjectFormats,
  attr,
  authEventDefinitions,
  courseSubjectFormats,
  createEventWithMetadata,
  defineAnalyticsSubject,
  defineClientEvent,
  defineServerEvent,
  eventsAbout,
  instituteEventDefinitions,
  instituteSubjectFormats,
  liveSessionSubjectFormats,
  nonNegativeInt,
  noneSubjectFormats,
  openEnum,
  payload,
  payloadByOutcome,
  portalEventDefinitions,
  privilegeEventDefinitions,
  privilegeGroupSubjectFormats,
  privilegeSubjectFormats,
  proctoringAssessmentConfigurationSubjectFormats,
  proctoringEventDefinitions,
  proctoringSessionSubjectFormats,
  reportsEventDefinitions,
  resultsEventDefinitions,
  schemaOf,
  storedSchemaOf,
  subjectOneOf,
  userSubjectFormats,
  uuidString
};
//# sourceMappingURL=index.js.map